import { Button } from "@msuite/ui/button";
import { TextInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { formatNumber } from "@msuite/utils/numbers";
import type { CalculationFormSchema } from "@msuite/utils/schemas";
import { calculatePositionTotal } from "@msuite/utils/services";
import type { ServiceUnit } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";
import { CalculationFormItemPositionFields } from "./calculation-form-item-position-fields";

/** Props Interface */
interface CalculationFormItemPositionProps {
	index: number;
	positionIndex: number;
	unit: ServiceUnit;
	unitPrice?: number | null;
	onRemove: () => void;
}

export function CalculationFormItemPosition({
	index,
	positionIndex,
	unit,
	unitPrice,
	onRemove,
}: CalculationFormItemPositionProps) {
	/** Context */
	const methods = useFormContext<CalculationFormSchema>();
	const position = useWatch({
		control: methods.control,
		name: `services.${index}.positions.${positionIndex}`,
	});

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const positionTotal = position ? calculatePositionTotal(unit, position, unitPrice) : 0;

	/** Render */
	return (
		<div className="items-end h-stack">
			<div className="w-[450px]">
				<TextInput
					isRequired
					label={t("calculations.position_name")}
					control={methods.control}
					name={`services.${index}.positions.${positionIndex}.name`}
					placeholder={t("calculations.position_name")}
					errorVariant="tooltip"
				/>
			</div>
			<div className="h-stack w-full">
				<CalculationFormItemPositionFields
					unit={unit}
					index={index}
					positionIndex={positionIndex}
				/>
			</div>
			<div className="w-[250px]">
				<div className="input border-dashed w-full">
					<p>{formatNumber(positionTotal, "currency")}</p>
				</div>
			</div>
			<Button
				icon={
					<Icon
						name="Trash2"
						className="stroke-red-500"
					/>
				}
				variant="white"
				size="sm"
				onClick={onRemove}
			/>
		</div>
	);
}
